import {
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  GroupProps,
  IndicatorsContainerProps,
  IndicatorSeparatorProps,
  MenuProps,
  MultiValueProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';
import { ClassNamesConfig } from 'react-select/dist/declarations/src/styles';

import cx from 'clsx';

import styles from './Select.module.scss';

type GetClassName<State> = {
  state: State;
  className: string;
  propsClassName?: (state: State) => string;
};

function getClassName<State>({ state, className, propsClassName }: GetClassName<State>) {
  return cx(className, propsClassName && propsClassName(state));
}

export function mixClassNames<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  classNames: ClassNamesConfig<Option, IsMulti, Group>
): ClassNamesConfig<Option, IsMulti, Group> {
  const {
    valueContainer,
    menu,
    control,
    placeholder,
    indicatorsContainer,
    indicatorSeparator,
    option,
    multiValueLabel,
    multiValueRemove,
    singleValue,
    group,
    clearIndicator,
    dropdownIndicator,
    ...restClassNames
  } = classNames;

  return {
    control: (state) =>
      getClassName<ControlProps<Option, IsMulti, Group>>({
        state,
        className: cx(
          styles.select__control,
          state.isFocused && styles['select__control--is-focused'],
          state.isDisabled && styles['select__control--is-disabled']
        ),
        propsClassName: control,
      }),
    group: (state) =>
      getClassName<GroupProps<Option, IsMulti, Group>>({
        state,
        className: styles.select__group,
        propsClassName: group,
      }),
    valueContainer: (state) =>
      getClassName<ValueContainerProps<Option, IsMulti, Group>>({
        state,
        className: cx(
          styles['select__value-container'],
          state.hasValue && styles['select__value-container--has-value']
        ),
        propsClassName: valueContainer,
      }),
    menu: (state) =>
      getClassName<MenuProps<Option, IsMulti, Group>>({
        state,
        className: styles.select__menu,
        propsClassName: menu,
      }),
    placeholder: (state) =>
      getClassName<PlaceholderProps<Option, IsMulti, Group>>({
        state,
        className: styles.select__placeholder,
        propsClassName: placeholder,
      }),
    singleValue: (state) =>
      getClassName<SingleValueProps<Option, IsMulti, Group>>({
        state,
        className: styles['select__single-value'],
        propsClassName: singleValue,
      }),
    indicatorsContainer: (state) =>
      getClassName<IndicatorsContainerProps<Option, IsMulti, Group>>({
        state,
        className: styles.select__indicators,
        propsClassName: indicatorsContainer,
      }),
    indicatorSeparator: (state) =>
      getClassName<IndicatorSeparatorProps<Option, IsMulti, Group>>({
        state,
        className: styles['select__separator-indicator'],
        propsClassName: indicatorSeparator,
      }),
    dropdownIndicator: (state) =>
      getClassName<DropdownIndicatorProps<Option, IsMulti, Group>>({
        state,
        className: styles['select__dropdown-indicator'],
        propsClassName: dropdownIndicator,
      }),
    clearIndicator: (state) =>
      getClassName<ClearIndicatorProps<Option, IsMulti, Group>>({
        state,
        className: styles['select__clear-indicator'],
        propsClassName: clearIndicator,
      }),
    option: (state) =>
      getClassName<OptionProps<Option, IsMulti, Group>>({
        state,
        className: cx(
          styles.select__option,
          state.isSelected && styles['select__option--is-selected'],
          state.isFocused && styles['select__option--is-focused']
        ),
        propsClassName: option,
      }),
    multiValueLabel: (state) =>
      getClassName<MultiValueProps<Option, IsMulti, Group>>({
        state,
        className: styles['select__multi-value__label'],
        propsClassName: multiValueLabel,
      }),
    multiValueRemove: (state) =>
      getClassName<MultiValueProps<Option, IsMulti, Group>>({
        state,
        className: styles['select__multi-value__remove'],
        propsClassName: multiValueRemove,
      }),
    ...restClassNames,
  };
}
